// Cursor-paginatie aan clientzijde: haalt ELKE pagina van een gescopet endpoint op
// en plakt ze aan elkaar. De gebruiker ziet dus altijd de VOLLEDIGE set — alleen
// niet in één respons.
//
// WAAROM DIT BESTAAT: een lijst-endpoint dat alles in één keer teruggeeft groeit
// mee met de data. Bij tientallen MB's overleeft zo'n antwoord niet elke
// verbinding: de body komt afgekapt aan en JSON.parse struikelt met
// "Expected ',' or '}' after property value in JSON at position <einde>" — een
// fout die per gebruiker en per moment verschilt en het scherm leeg laat. In
// brokken blijft elk antwoord klein, en een brok die misgaat wordt gewoon opnieuw
// gevraagd i.p.v. de hele set te laten sneuvelen.
//
// VEILIGHEID: de server filtert eerst op user-scope (energielabel / team /
// verkoper) en pagineert pas daarna, dus doorvragen levert NOOIT records buiten
// je rechten op — hoe vaak je ook doorvraagt of welke cursor je ook meestuurt.
//
// Backwards-compat: geeft de server een platte array terug (oude vorm), dan
// stoppen we meteen en geven we die terug.

// Maximaal aantal pagina's dat één ophaalactie mag kosten. Puur een noodrem tegen
// een server-bug die eeuwig `has_more` blijft zeggen — bereiken we 'm, dan is dat
// een FOUT en geen stilzwijgend "klaar" (zie de throw onderaan de lus). Met
// PAGE_LIMIT=1000 dekt dit 2 miljoen records.
const MAX_PAGES = 2000;
const PAGE_LIMIT = 1000;
// Aantal pogingen per pagina. Een afgebroken of afgekapte respons is een
// verbindingsprobleem, geen datafout: opnieuw vragen lost het meestal op. Zonder
// deze retry gooit één hik onderweg de hele geladen set weg.
const PAGE_RETRIES = 3;
// Aantal vervolgpagina's dat tegelijk onderweg mag zijn. De server stuurt op de
// eerste pagina de cursors van álle vervolgpagina's mee (`page_cursors`), dus
// die hoeven niet meer één voor één op elkaar te wachten. Vier tegelijk: onder
// de zes verbindingen die een browser per host toestaat, zodat orders en
// facturen (die gelijktijdig laden) elkaar niet volledig blokkeren.
const PAGE_CONCURRENCY = 4;

const sleepMs = (ms) => new Promise(r => setTimeout(r, ms));

// Pagina-URL voor een cursor (null = eerste pagina).
const paginaUrl = (baseUrl, limit, cursor) => {
  const sep = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${sep}limit=${limit}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
};

const rijenVan = (j) => (Array.isArray(j.data) ? j.data : (Array.isArray(j.items) ? j.items : null));

async function fetchPaginated(baseUrl, token, opts = {}) {
  // onMeta: krijgt het ruwe antwoord per pagina, voor velden naast `data`
  // (bv. sync_running). Optioneel — andere aanroepers merken er niets van.
  // onPage(rows, meta): de rijen van élke pagina zodra die binnen is, ook de
  // parallelle (dus niet per se in volgorde). Voor een scherm dat de eerste
  // pagina meteen wil tonen en de rest aanvult; de volledige, geordende set
  // komt nog steeds als resultaat terug.
  const { limit = PAGE_LIMIT, signal, onMeta, onPage, concurrency = PAGE_CONCURRENCY } = opts;
  const all = [];
  let cursor = null;

  // Eén pagina ophalen, met herkansing. We onderscheiden bewust twee soorten fout:
  //   - 4xx (behalve 429): de server wijst ons af; opnieuw vragen helpt niet.
  //   - al de rest (netwerkfout, afgekapte body → JSON-parsefout, 5xx, 429):
  //     tijdelijk, dus opnieuw proberen met wat wachttijd ertussen.
  const haalPagina = async (url) => {
    let laatste;
    for (let poging = 1; poging <= PAGE_RETRIES; poging++) {
      try {
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, signal });
        if (!res.ok) {
          let msg = `HTTP ${res.status}`;
          // Toon ook het `detail` dat de server meestuurt (al merk-gesaneerd via
          // safeDetail, dus veilig voor elke rol). Zonder detail is een fout als
          // "Kon invoices niet ophalen" niet te diagnosticeren zonder serverlogs.
          try {
            const body = await res.json();
            msg = body.error || msg;
            if (body.detail) msg += ` — ${body.detail}`;
          } catch (_) { /* niet-JSON */ }
          const err = new Error(msg);
          err.status = res.status;
          if (res.status >= 400 && res.status < 500 && res.status !== 429) throw err;  // definitief
          laatste = err;
        } else {
          // res.json() is óók de plek waar een afgekapte body stukloopt
          // ("Expected ',' or '}' after property value in JSON at position …").
          // Die fout hoort dus binnen de retry te vallen, niet erbuiten.
          return await res.json();
        }
      } catch (e) {
        if (e?.name === 'AbortError' || e?.status) throw e;   // afgebroken of definitief
        laatste = e;
      }
      if (poging < PAGE_RETRIES) await sleepMs(300 * poging);
    }
    throw laatste || new Error('Pagina kon niet opgehaald worden');
  };

  // Alle vervolgpagina's uit `page_cursors` tegelijk (begrensd) ophalen, in de
  // volgorde van de lijst aan `all` plakken en de paginatie van de LAATSTE
  // teruggeven: zegt die toch nog has_more (de set groeide tussendoor), dan
  // loopt de gewone lus hieronder vanaf haar cursor verder.
  const haalParallel = async (cursors) => {
    const per = new Array(cursors.length);
    let volgende = 0;
    const werker = async () => {
      for (;;) {
        const k = volgende++;
        if (k >= cursors.length) return;
        const j = await haalPagina(paginaUrl(baseUrl, limit, cursors[k]));
        if (Array.isArray(j)) throw new Error('Onverwachte platte lijst bij een vervolgpagina');
        onMeta?.(j);
        { const rows = rijenVan(j); if (rows) onPage?.(rows, j); }
        per[k] = j;
      }
    };
    await Promise.all(Array.from({ length: Math.min(concurrency, cursors.length) }, werker));
    for (const j of per) { const rows = rijenVan(j); if (rows) all.push(...rows); }
    return per[per.length - 1].pagination || {};
  };

  for (let i = 0; i < MAX_PAGES; i++) {
    const j = await haalPagina(paginaUrl(baseUrl, limit, cursor));
    // Legacy shape: server retourneert direct een array
    if (Array.isArray(j)) { all.push(...j); return all; }
    onMeta?.(j);
    // `data` is de standaardvorm (orders/invoices/credit-notes/billshock);
    // /helpdesk/tickets houdt zijn veldnaam `items` omdat het antwoord daarnaast
    // eigen meta draagt (complete/truncated/cached).
    const rows = rijenVan(j);
    if (rows) { all.push(...rows); onPage?.(rows, j); }
    let pag = j.pagination || {};
    if (!pag.has_more || !pag.next_cursor) return all;
    // Eerste pagina met de cursors van alle vervolgpagina's → parallel. Een
    // server zonder die lijst (oudere versie) blijft gewoon pagina na pagina gaan.
    if (Array.isArray(pag.page_cursors) && pag.page_cursors.length && pag.page_cursors[0] === pag.next_cursor) {
      pag = await haalParallel(pag.page_cursors);
      if (!pag.has_more || !pag.next_cursor) return all;
    }
    // Een cursor die niet opschuift zou ons eeuwig dezelfde pagina laten ophalen
    // (en records dubbel tellen). Beter hard falen dan stilletjes verkeerde data.
    if (pag.next_cursor === cursor) throw new Error('Paginatie loopt vast: de server geeft dezelfde cursor terug');
    cursor = pag.next_cursor;
  }
  // Hier komen we alleen bij een bug. NIET `return all`: dan zou het scherm een
  // onvolledige set tonen alsof ze compleet is, en dat mag nooit stil gebeuren.
  throw new Error(`Te veel pagina's (>${MAX_PAGES}) bij het ophalen van ${baseUrl} — data mogelijk onvolledig, herlaad de pagina`);
}

export { fetchPaginated, MAX_PAGES, PAGE_LIMIT, PAGE_RETRIES, PAGE_CONCURRENCY };
